/**
 * First-run onboarding: a brand-new account lands here instead of staring at an
 * empty calendar. Three explicit steps — connect → create → review — teach the
 * value proposition through use. The activation moment is the first successful
 * multi-platform publish, not account creation.
 *
 * `step` is the current step (1 = connect, 2 = create); the parent derives it
 * from live state and renders this only while step > 0.
 */
const STEPS = [
  {
    n: 1,
    title: "Connect your channels",
    desc: "Link YouTube, TikTok, Instagram, Facebook, or X — connect as many as you publish to.",
    cta: "Connect a channel",
  },
  {
    n: 2,
    title: "Create your first post",
    desc: "One idea in. Broadside tailors a native version for every channel you connected.",
    cta: "Create your first post",
  },
  {
    n: 3,
    title: "Review & publish",
    desc: "Check each platform’s version, then publish everywhere at once — and watch it land.",
    cta: null,
  },
] as const;

export function Onboarding({
  step,
  onConnect,
  onCreate,
}: {
  step: 1 | 2;
  onConnect: () => void;
  onCreate: () => void;
}) {
  return (
    <section className="onboard" aria-label="Getting started">
      <div className="onboard-head">
        <h2>Get set up</h2>
        <span className="onboard-progress">Step {step} of 3</span>
      </div>
      <ol className="onboard-steps">
        {STEPS.map((s) => {
          const state = s.n < step ? "done" : s.n === step ? "active" : "todo";
          return (
            <li key={s.n} className={`onboard-step is-${state}`}>
              <span className="onboard-step-n" aria-hidden="true">
                {state === "done" ? "✓" : s.n}
              </span>
              <div className="onboard-step-body">
                <div className="onboard-step-title">{s.title}</div>
                <div className="onboard-step-desc">{s.desc}</div>
                {s.n === step && s.cta && (
                  <button
                    type="button"
                    className="btn primary small onboard-cta"
                    onClick={s.n === 1 ? onConnect : onCreate}
                  >
                    {s.cta}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
